// eslint-disable max-lines
import { MoveValidationError } from "../../errors";
import { isCapturable, isProductionBuilding } from "../buildings";
import { canBuildUnitKind } from "../capacity";
import { assertAntiAirBuildAllowed } from "../economy";
import { coordEq, getTile, inBounds, manhattan, unitAt } from "../grid";
import { canLoadInto, canUnloadAt, isTransport } from "../transport";
import type { Coord, GameState, Move, Unit } from "../types";
import { isBuildingTile } from "../types";
import {
  BUILDABLE_BY_BUILDING,
  UNIT_DEFS,
  canMoveAndAttackSameTurn,
  canUnitDamage,
  isUnitInAttackRange,
} from "../units";
import {
  assertGameInProgress,
  assertReachable,
  findActingUnit,
  pathDestination,
  canJoinUnits,
} from "./helpers";

function validateBuildMove(state: GameState, move: Extract<Move, { type: "build" }>): void {
  const tile = getTile(state, move.pos);
  if (!isBuildingTile(tile.kind))
    throw new MoveValidationError("no_building", `No building at ${move.pos.x},${move.pos.y}`);
  if (!isProductionBuilding(tile.kind))
    throw new MoveValidationError("not_buildable_here", `Can only build at a production building`);
  if (tile.owner !== state.currentPlayer)
    throw new MoveValidationError("wrong_owner", `Building not owned by ${state.currentPlayer}`);
  if (unitAt(state, move.pos))
    throw new MoveValidationError("building_occupied", `Building tile is occupied`);
  if (!UNIT_DEFS[move.unitKind])
    throw new MoveValidationError("unknown_unit_kind", `Unknown unit kind ${move.unitKind}`);
  const allowedKinds = BUILDABLE_BY_BUILDING[tile.kind] as string[];
  if (!allowedKinds.includes(move.unitKind))
    throw new MoveValidationError(
      "not_buildable_here",
      `Cannot build ${move.unitKind} at ${tile.kind}`,
    );
  if (state.funds[state.currentPlayer] < UNIT_DEFS[move.unitKind].cost)
    throw new MoveValidationError("insufficient_funds", `Insufficient funds`);
  assertAntiAirBuildAllowed({ state, unitKind: move.unitKind });
  if (
    !canBuildUnitKind({
      state,
      player: state.currentPlayer,
      unitKind: move.unitKind,
    })
  ) {
    throw new MoveValidationError(
      "capacity_reached",
      `Capacity reached for ${UNIT_DEFS[move.unitKind].movementType} units`,
    );
  }
}

function validateWaitMove(unit: Unit): void {
  if (isTransport(unit.kind) && unit.cargo && unit.hasMoved && !unit.unloadReady) {
    throw new MoveValidationError(
      "cannot_unload",
      `Unit ${unit.id} must prepare unload before waiting`,
    );
  }
}

function validatePrepareUnloadMove(unit: Unit): void {
  if (!isTransport(unit.kind))
    throw new MoveValidationError("not_transport", `Unit ${unit.id} is not a transport`);
  if (!unit.cargo) throw new MoveValidationError("no_cargo", `Unit ${unit.id} has no cargo`);
  if (unit.hasAttacked)
    throw new MoveValidationError("already_attacked", `Unit ${unit.id} already finished its turn`);
  if (unit.unloadReady)
    throw new MoveValidationError("cannot_unload", `Unit ${unit.id} already prepared to unload`);
}

function validateUnloadMove({
  state,
  unit,
  pos,
}: {
  state: GameState;
  unit: Unit;
  pos: Coord;
}): void {
  if (!isTransport(unit.kind))
    throw new MoveValidationError("not_transport", `Unit ${unit.id} is not a transport`);
  if (!canUnloadAt({ state, transport: unit, pos })) {
    throw new MoveValidationError("cannot_unload", `Cannot unload at (${pos.x}, ${pos.y})`);
  }
}

function validateLoadMove({
  state,
  unit,
  move,
}: {
  state: GameState;
  unit: Unit;
  move: Extract<Move, { type: "load" }>;
}): void {
  if (unit.hasMoved || unit.hasAttacked) {
    throw new MoveValidationError("already_moved", `Unit ${unit.id} already acted this turn`);
  }
  const transport = state.units.find((u) => u.id === move.transportId);
  if (!transport)
    throw new MoveValidationError("unit_not_found", `Unknown transport ${move.transportId}`);
  if (!canLoadInto({ state, passenger: unit, transport })) {
    throw new MoveValidationError(
      "cannot_load",
      `Unit ${unit.id} cannot load into ${move.transportId}`,
    );
  }
  if (move.path.length === 0) {
    throw new MoveValidationError("path_empty", "Load path is empty");
  }
  const dest = pathDestination(move.path, unit.pos);
  if (!coordEq(dest, transport.pos)) {
    throw new MoveValidationError(
      "path_wrong_endpoint",
      `Load path must end at transport position`,
    );
  }
  assertReachable({ state, unit, dest, options: { allowOccupiedDestination: true } });
}

function validateCaptureMove(state: GameState, unit: Unit): void {
  if (!UNIT_DEFS[unit.kind].canCapture)
    throw new MoveValidationError("cannot_capture", `Unit ${unit.id} cannot capture`);
  const tile = getTile(state, unit.pos);
  if (!isBuildingTile(tile.kind))
    throw new MoveValidationError("no_building", `No building at unit position`);
  if (!isCapturable(tile, unit.owner))
    throw new MoveValidationError("already_owned", `Building already owned by ${unit.owner}`);
}

function validateMovePath({
  state,
  unit,
  path,
  options,
}: {
  state: GameState;
  unit: Unit;
  path: Coord[];
  options?: { allowOccupiedDestination?: boolean };
}): Coord {
  if (path.length === 0) {
    throw new MoveValidationError("path_empty", "Move path is empty");
  }
  const dest = pathDestination(path, unit.pos);
  if (!inBounds(state, dest)) {
    throw new MoveValidationError("out_of_bounds", `Destination out of bounds`);
  }
  assertReachable({ state, unit, dest, options });
  const occupant = unitAt(state, dest);
  if (occupant && occupant.id !== unit.id) {
    throw new MoveValidationError("destination_occupied", `Destination occupied`);
  }
  return dest;
}

function assertAttackPathIfPresent({
  state,
  unit,
  move,
}: {
  state: GameState;
  unit: Unit;
  move: Extract<Move, { type: "attack" }>;
}): Coord {
  let attackerPos: Coord = unit.pos;
  if (!move.path || move.path.length === 0) return attackerPos;
  if (unit.hasMoved) {
    throw new MoveValidationError("already_moved", `Unit ${unit.id} cannot move before attacking`);
  }
  if (!canMoveAndAttackSameTurn(unit.kind)) {
    throw new MoveValidationError(
      "indirect_after_move",
      `Unit ${unit.id} cannot attack after moving`,
    );
  }
  attackerPos = pathDestination(move.path, unit.pos);
  assertReachable({ state, unit, dest: attackerPos });
  const occupant = unitAt(state, attackerPos);
  if (occupant && occupant.id !== unit.id) {
    throw new MoveValidationError("destination_occupied", `Attack path destination occupied`);
  }
  return attackerPos;
}

function validateAttackMove({
  state,
  unit,
  move,
}: {
  state: GameState;
  unit: Unit;
  move: Extract<Move, { type: "attack" }>;
}): void {
  if (unit.hasAttacked)
    throw new MoveValidationError("already_attacked", `Unit ${unit.id} already attacked`);
  if (unit.unloadReady) {
    throw new MoveValidationError(
      "preparing_unload",
      `Unit ${unit.id} cannot attack while preparing to unload`,
    );
  }
  if (!canMoveAndAttackSameTurn(unit.kind) && unit.hasMoved) {
    throw new MoveValidationError(
      "indirect_after_move",
      `Unit ${unit.id} cannot fire after moving`,
    );
  }
  const target = state.units.find((u) => u.id === move.targetId);
  if (!target) throw new MoveValidationError("unit_not_found", `Unknown target ${move.targetId}`);
  if (target.owner === unit.owner)
    throw new MoveValidationError("ally_target", `Cannot attack ally`);

  const attackerPos = assertAttackPathIfPresent({ state, unit, move });
  const distance = manhattan(attackerPos, target.pos);
  if (!isUnitInAttackRange(unit, distance)) {
    throw new MoveValidationError("out_of_range", `Target out of range`);
  }
  if (!canUnitDamage({ unit, defenderKind: target.kind })) {
    throw new MoveValidationError(
      "cannot_damage_armor",
      `Cannot attack that unit (no damage vs its kind)`,
    );
  }
  getTile(state, target.pos);
}

function validateJoinMove({
  state,
  unit,
  move,
}: {
  state: GameState;
  unit: Unit;
  move: Extract<Move, { type: "join" }>;
}): void {
  if (unit.hasMoved)
    throw new MoveValidationError("already_moved", `Unit ${unit.id} already moved`);
  const target = state.units.find((u) => u.id === move.targetId);
  if (!target) throw new MoveValidationError("unit_not_found", `Unknown target ${move.targetId}`);
  if (!canJoinUnits(unit, target)) {
    throw new MoveValidationError(
      "cannot_join",
      `Unit ${unit.id} cannot join target ${move.targetId}`,
    );
  }
  const dest = pathDestination(move.path, unit.pos);
  if (!coordEq(dest, target.pos)) {
    throw new MoveValidationError("path_wrong_endpoint", `Join path must end at target position`);
  }
  assertReachable({ state, unit, dest, options: { allowOccupiedDestination: true } });
}

export function validateMove(state: GameState, move: Move): void {
  assertGameInProgress(state);
  if (move.type === "endTurn") return;

  if (move.type === "build") {
    validateBuildMove(state, move);
    return;
  }

  const unit = findActingUnit(state, move.unitId);

  switch (move.type) {
    case "wait":
      validateWaitMove(unit);
      return;
    case "prepareUnload":
      validatePrepareUnloadMove(unit);
      return;
    case "unload":
      validateUnloadMove({ state, unit, pos: move.pos });
      return;
    case "load":
      validateLoadMove({ state, unit, move });
      return;
    case "capture":
      validateCaptureMove(state, unit);
      return;
    case "move":
      if (unit.hasMoved)
        throw new MoveValidationError("already_moved", `Unit ${unit.id} already moved`);
      validateMovePath({ state, unit, path: move.path });
      return;
    case "attack":
      validateAttackMove({ state, unit, move });
      return;
    case "join":
      validateJoinMove({ state, unit, move });
      return;
  }
}
